import React, { useState, useEffect } from 'react';
import axios from '../../../configApi/axiosConfig';
import { APIURL } from '../../../configApi/apiConfig';

export function FormularioProtocolo({ 
    isOpen, 
    onClose, 
    onSubmit, 
    editData = null,
    riesgoData = [],
    accionesDisponibles = []
}) {
    const [formData, setFormData] = useState({
        desde: '',
        hasta: '',
        descripcion: '',
        riesgo: '',
        cartera: '',
        idCbo_Acciones: '',
        dias_mora: 0
    });

    const [errors, setErrors] = useState({});
    const [carteraData, setCarteraData] = useState([]);
    const [loadingCartera, setLoadingCartera] = useState(false);
    const [carteraSelected, setCarteraSelected] = useState(null); // Cartera con su rango de días

    // Cartera options - Ya no usamos este array estático
    // const carteraOptions = [
    //     { value: 'PREVENTIVA', label: 'Preventiva' },
    //     ...
    // ];

    useEffect(() => {
        if (editData) {
            // Modo edición - cargar datos existentes
            const accionObj = accionesDisponibles.find(a => 
                a.Accion && editData.accion && a.Accion.toUpperCase() === editData.accion.toUpperCase()
            );

            setFormData({
                desde: editData.desde || '',
                hasta: editData.hasta || '',
                descripcion: editData.descripcion || '',
                riesgo: editData.riesgo || '',
                cartera: editData.cartera || '',
                idCbo_Acciones: accionObj ? accionObj.idCbo_Acciones : '',
                dias_mora: editData.dias_mora || 0
            });
        } else {
            // Modo crear - limpiar formulario
            resetForm();
        }
    }, [editData, accionesDisponibles]);

    // Cargar carteras cuando se selecciona la fecha desde
    useEffect(() => {
        if (formData.desde) {
            fetchCarteraData();
            // Limpiar cartera seleccionada si existe
            setFormData(prev => ({
                ...prev,
                cartera: ''
            }));
        } else {
            setCarteraData([]);
            setFormData(prev => ({
                ...prev,
                cartera: ''
            }));
        }
    }, [formData.desde]);

    const fetchCarteraData = async () => {
        try {
            setLoadingCartera(true);
            // Extraer el período de la fecha (YYYY-MM)
            const period = formData.desde.substring(0, 7);
            const response = await axios.get(APIURL.cbo_tipoCarteraFindAll(formData.desde));
            setCarteraData(response.data || []);
            setCarteraSelected(null);
        } catch (error) {
            console.error('Error al cargar carteras:', error);
            setCarteraData([]);
            setCarteraSelected(null);
        } finally {
            setLoadingCartera(false);
        }
    };

    const resetForm = () => {
        setFormData({
            desde: '',
            hasta: '',
            descripcion: '',
            riesgo: '',
            cartera: '',
            idCbo_Acciones: '',
            dias_mora: 0
        });
        setErrors({});
    };

    const getFirstDayOfMonth = (dateString) => {
        if (!dateString) return null;
        // Extraer año y mes del string YYYY-MM-DD
        const [year, month] = dateString.split('-');
        return `${year}-${month}-01`;
    };

    const getLastDayOfMonth = (dateString) => {
        if (!dateString) return null;
        // Extraer año y mes del string YYYY-MM-DD
        const [year, month] = dateString.split('-');
        // Calcular el último día del mes
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        return `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
    };

    const isSameMonthYear = (date1, date2) => {
        if (!date1 || !date2) return true;
        // Comparar directamente los strings YYYY-MM
        const yearMonth1 = date1.substring(0, 7);
        const yearMonth2 = date2.substring(0, 7);
        return yearMonth1 === yearMonth2;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Si es cartera, guardar también el rango de días
        if (name === 'cartera') {
            const selectedCartera = carteraData.find(c => (c.Cartera || c.nombreTipoCartera) === value);
            if (selectedCartera) {
                setCarteraSelected({
                    nombre: value,
                    desde: selectedCartera.Desde,
                    hasta: selectedCartera.Hasta
                });
            }
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Limpiar error del campo al cambiar
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.desde) newErrors.desde = 'Fecha desde es requerida';
        if (!formData.hasta) newErrors.hasta = 'Fecha hasta es requerida';
        if (!formData.riesgo) newErrors.riesgo = 'Riesgo es requerido';
        if (!formData.cartera) newErrors.cartera = 'Cartera es requerida';
        if (!formData.idCbo_Acciones) newErrors.idCbo_Acciones = 'Acción es requerida';

        // Validar que fecha desde sea menor o igual a fecha hasta
        if (formData.desde && formData.hasta && formData.desde > formData.hasta) {
            newErrors.hasta = 'La fecha hasta debe ser mayor o igual a la fecha desde';
        }

        // Validar que ambas fechas estén en el mismo mes y año
        if (formData.desde && formData.hasta && !isSameMonthYear(formData.desde, formData.hasta)) {
            newErrors.hasta = 'Las fechas deben estar en el mismo mes y año';
        }

        // Validar días de mora
        if (formData.dias_mora < 0) {
            newErrors.dias_mora = 'Los días de mora no pueden ser negativos';
        }

        // Validar que días de mora esté dentro del rango de la cartera seleccionada
        if (carteraSelected) {
            const diasMin = carteraSelected.desde;
            const diasMax = carteraSelected.hasta;
            if (formData.dias_mora < diasMin || formData.dias_mora > diasMax) {
                newErrors.dias_mora = `Los días de mora deben estar entre ${diasMin} y ${diasMax} para la cartera seleccionada`;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validate()) {
            return;
        }

        // Obtener el nombre de la acción seleccionada
        const accionObj = accionesDisponibles.find(a => a.idCbo_Acciones == formData.idCbo_Acciones);
        
        const dataToSubmit = {
            ...formData,
            accion: accionObj ? accionObj.Accion : '',
            dias_mora: parseInt(formData.dias_mora)
        };

        onSubmit(dataToSubmit);
        resetForm();
        onClose();
    };

    const handleCancel = () => {
        resetForm();
        onClose();
    };

    const getInternoExternoLabel = (value) => {
        return value === 1 ? 'INTERNO' : value === 2 ? 'EXTERNO' : '';
    };

    const getTipoLabel = (value) => {
        return value === 1 ? 'TELEFONICO' : value === 2 ? 'TERRENO' : value === 3 ? 'TELEMATICO' : '';
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-6 rounded-t-3xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white bg-opacity-20 rounded-xl">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-white">
                                {editData ? '✏️ Editar Protocolo' : '➕ Nuevo Protocolo'}
                            </h2>
                        </div>
                        <button
                            onClick={handleCancel}
                            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-all"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Form Body */}
                <form onSubmit={handleSubmit} className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Fecha Desde */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                📅 Fecha Desde *
                            </label>
                            <input
                                type="date"
                                name="desde"
                                value={formData.desde}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                                    errors.desde 
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                                }`}
                            />
                            {errors.desde && <p className="text-red-500 text-xs mt-1">{errors.desde}</p>}
                        </div>

                        {/* Fecha Hasta */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                📅 Fecha Hasta *
                            </label>
                            <input
                                type="date"
                                name="hasta"
                                value={formData.hasta}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                                    errors.hasta 
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                                }`}
                            />
                            {errors.hasta && <p className="text-red-500 text-xs mt-1">{errors.hasta}</p>}
                            {(formData.desde || formData.hasta) && (
                                <p className="text-indigo-600 text-xs mt-1">
                                    💡 Recuerda: Ambas fechas deben estar en el mismo mes y año
                                </p>
                            )}
                        </div>

                        {/* Riesgo */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                ⚠️ Riesgo *
                            </label>
                            <select
                                name="riesgo"
                                value={formData.riesgo}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                                    errors.riesgo 
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                                }`}
                            >
                                <option value="">Seleccione un riesgo</option>
                                {riesgoData && riesgoData.map(r => (
                                    <option key={r.idCbo_Riesgo || r.id} value={r.Riesgo || r.nombre}>
                                        {r.Riesgo || r.nombre}
                                    </option>
                                ))}
                            </select>
                            {errors.riesgo && <p className="text-red-500 text-xs mt-1">{errors.riesgo}</p>}
                        </div>

                        {/* Cartera */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                💼 Cartera {!formData.desde && <span className="text-red-500">*</span>} {loadingCartera && '(Cargando...)'}
                            </label>
                            <select
                                name="cartera"
                                value={formData.cartera}
                                onChange={handleChange}
                                disabled={!formData.desde || loadingCartera}
                                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                                    !formData.desde
                                        ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                                        : errors.cartera 
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                                }`}
                            >
                                <option value="">{!formData.desde ? 'Primero selecciona la fecha DESDE' : 'Seleccione una cartera'}</option>
                                {carteraData && carteraData.length > 0 ? (
                                    carteraData.map(cartera => (
                                        <option key={cartera.idCbo_TipoCartera || cartera.id} value={cartera.Cartera || cartera.nombreTipoCartera}>
                                            {cartera.Cartera || cartera.nombreTipoCartera} (Días: {cartera.Desde} - {cartera.Hasta})
                                        </option>
                                    ))
                                ) : (
                                    !loadingCartera && formData.desde && <option disabled>No hay carteras disponibles</option>
                                )}
                            </select>
                            {errors.cartera && <p className="text-red-500 text-xs mt-1">{errors.cartera}</p>}
                            {formData.desde && <p className="text-indigo-600 text-xs mt-1">💡 Carteras disponibles para el período {formData.desde.substring(0, 7)}</p>}
                        </div>

                        {/* Acción */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                🎯 Acción *
                            </label>
                            <select
                                name="idCbo_Acciones"
                                value={formData.idCbo_Acciones}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                                    errors.idCbo_Acciones 
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                                }`}
                            >
                                <option value="">Seleccione una acción</option>
                                {accionesDisponibles && accionesDisponibles.map(accion => (
                                    <option key={accion.idCbo_Acciones} value={accion.idCbo_Acciones}>
                                        {accion.Accion} - {getInternoExternoLabel(accion.InternoExterno)} - {getTipoLabel(accion.Tipo)}
                                    </option>
                                ))}
                            </select>
                            {errors.idCbo_Acciones && <p className="text-red-500 text-xs mt-1">{errors.idCbo_Acciones}</p>}
                        </div>

                        {/* Días de Mora */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                ⏱️ Días de Mora {carteraSelected && <span className="text-gray-500 text-xs">(Rango: {carteraSelected.desde} - {carteraSelected.hasta})</span>}
                            </label>
                            <input
                                type="number"
                                name="dias_mora"
                                value={formData.dias_mora}
                                onChange={handleChange}
                                min={carteraSelected ? carteraSelected.desde : 0}
                                max={carteraSelected ? carteraSelected.hasta : undefined}
                                disabled={!formData.cartera}
                                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                                    !formData.cartera
                                        ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                                        : errors.dias_mora 
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                                }`}
                            />
                            {errors.dias_mora && <p className="text-red-500 text-xs mt-1">{errors.dias_mora}</p>}
                            {carteraSelected && <p className="text-indigo-600 text-xs mt-1">📌 Debes seleccionar un valor entre {carteraSelected.desde} y {carteraSelected.hasta} días</p>}
                        </div>

                        {/* Descripción */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                📝 Descripción
                            </label>
                            <textarea
                                name="descripcion"
                                value={formData.descripcion}
                                onChange={handleChange}
                                rows="3"
                                placeholder="Ingrese una descripción opcional..."
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-200 transition-all resize-none"
                            />
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-4 mt-8">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200 border-2 border-gray-200"
                        >
                            ✖️ Cancelar
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            {editData ? '💾 Guardar Cambios' : '➕ Crear Protocolo'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
